import type { Object3D } from "three/webgpu";

/** How long the sweep that draws a model in takes, in seconds. */
export const REVEAL_SECONDS = 2.6;
/** How long a burst of sparks lives. */
export const SPARK_SECONDS = 1.4;

/**
 * Where the sweep's front is, in the model's own height units, `t` seconds after it began: from a little
 * under the floor to a little over the roof, eased in and out. Null once it has passed.
 */
export function revealFront(t: number, height: number): number | null {
  if (t < 0 || t >= REVEAL_SECONDS) return null;
  const p = t / REVEAL_SECONDS;
  const eased = p * p * (3 - 2 * p);
  return -0.05 * height + eased * height * 1.1;
}

/** How far through its life a burst is, 0 to 1, `t` seconds after it began; null before and after. */
export function sparkLife(t: number): number | null {
  return t < 0 || t >= SPARK_SECONDS ? null : t / SPARK_SECONDS;
}

/** An object's name as the model authored it: three's loader keeps it in `userData` and sanitizes `name`. */
export function authoredName(o: Object3D): string {
  const original = (o.userData as { name?: unknown }).name;
  return typeof original === "string" && original.length > 0 ? original : o.name;
}

/**
 * The part an object belongs to: the nearest ancestor (itself included) named in `parts`, compared
 * without case - or, with `parts` null, the nearest ancestor with a name at all, below `root`.
 */
export function partOf(object: Object3D, parts: readonly string[] | null, root: Object3D | null = null): string | null {
  const wanted = parts ? new Set(parts.map((p) => p.toLowerCase())) : null;
  for (let o: Object3D | null = object; o && o !== root; o = o.parent) {
    const name = authoredName(o);
    if (!name) continue;
    if (!wanted || wanted.has(name.toLowerCase())) return name;
  }
  return null;
}

/** Every mesh belonging to `part`, as `partOf` reckons it. */
export function meshesOf(root: Object3D, part: string, parts: readonly string[] | null): Object3D[] {
  const found: Object3D[] = [];
  const lower = part.toLowerCase();
  root.traverse((o) => {
    if ((o as { isMesh?: boolean }).isMesh && partOf(o, parts, root)?.toLowerCase() === lower) found.push(o);
  });
  return found;
}
